import type { MetaFunction, LinksFunction } from "@remix-run/node";
import { Link } from "@remix-run/react";
import {
  getSeoMeta,
  getOrganizationJsonLd,
  getAppWithReviewsJsonLd,
  getFaqPageJsonLd,
} from "~/utils/seo-meta";
import { SEO_ROUTE_GROUPS } from "~/utils/seo-routes";
import { lazy, Suspense, useEffect, useState } from "react";
import { Plans } from "~/components/plans";

// Logos — keep the real Prizo brand mark + real merchant logos
import prizoLogoColor from "../assets/prizo-logo-color.svg";
import logo1 from "../assets/logos/logo1.webp";
import logo2 from "../assets/logos/logo2.webp";
import logo3 from "../assets/logos/logo3.avif";
import logo4 from "../assets/logos/logo4.png";
import icecoLogo from "../assets/logos/ICECO_LOGO.webp";
import rinkitLogo from "../assets/logos/Rinkit.avif";

const SHOPIFY_APP_URL = "https://apps.shopify.com/prizo";
const CALENDLY_URL = "https://calendly.com/nadav-prizo/30min";

// Plus Jakarta Sans for the <Plans /> component to match the design
const plansFontClass = "font-['Plus_Jakarta_Sans']";

// Shared so the homepage (_index) can render the same landing experience at "/".
// Both "/" and "/landing" declare canonical "/" so search engines consolidate them.
export const LANDING_META = {
  title: "Prizo — Spin the Wheel & Gamification App for Shopify",
  description:
    "Prizo is the Shopify spin the wheel & gamification app — add spin the wheel, scratch cards, plinko & more to capture emails, reduce cart abandonment, and boost repeat sales. Free to install.",
} as const;

// Homepage FAQ — answers the real questions merchants search (validated in
// Semrush: cart-abandonment how-tos, popup/email capture, gamification). Doubles
// as FAQPage structured data and helps AI search engines cite Prizo.
const LANDING_FAQ = [
  {
    question: "What is Prizo?",
    answer:
      "Prizo is a Shopify app that adds gamified popups and post-purchase games — like spin the wheel, scratch cards, plinko, and pick a box — to your store. Shoppers play to win discounts and rewards, which helps you capture emails, reduce cart abandonment, and turn one-time buyers into repeat customers. It installs from the Shopify App Store with no code.",
  },
  {
    question: "How does Prizo reduce cart abandonment and increase sales?",
    answer:
      "Prizo gives shoppers a fun, rewarding reason to act in the moment — on store entry and on the post-purchase thank-you page. The anticipation of playing a game and winning a real reward reduces hesitation, encourages completed checkouts, and seeds a next-order incentive that brings customers back, lifting repeat purchases and average order value.",
  },
  {
    question: "Which games does Prizo offer?",
    answer:
      "Prizo includes spin the wheel, scratch card, pick a box, plinko, claw machine, basketball, hit the target, lucky dice, and break the ice — all in one app. You can run one or several, A/B test them, and rotate games for seasonal campaigns without installing another app.",
  },
  {
    question: "Is Prizo free?",
    answer:
      "Prizo is free to install from the Shopify App Store and offers a free trial, with paid plans as you scale. You can launch a game and start capturing emails and rewarding shoppers before committing to a paid plan.",
  },
  {
    question: "Does Prizo work with Shopify checkout?",
    answer:
      "Yes. Prizo works natively with Shopify on the post-purchase thank-you page and supports the Shopify Checkout UI where extensions are allowed. Setup is no-code — there are no theme edits and no developer required.",
  },
  {
    question: "Can Prizo grow my email and SMS list?",
    answer:
      "Yes. Shoppers enter their email or phone number to play and claim their reward, so you capture engaged, opted-in subscribers in a moment of genuine interest. Prizo syncs with email platforms like SendGrid and Klaviyo so contacts flow straight into your marketing.",
  },
] as const;

export const landingLinks: LinksFunction = () => [
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,600;12..96,700;12..96,800&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap",
  },
];

export const meta: MetaFunction = () => {
  return getSeoMeta({
    ...LANDING_META,
    url: "/",
  });
};

export const links: LinksFunction = landingLinks;

const PAGE_CSS = `
  .prizo-root { font-family: 'Plus Jakarta Sans', system-ui, sans-serif; background: #FBFAFF; color: #14112E; -webkit-font-smoothing: antialiased; }
  .prizo-root ::selection { background: #696CFF; color: #fff; }
  html { scroll-behavior: smooth; }
  .prizo-root section[id], .prizo-root header[id] { scroll-margin-top: 84px; }
  @keyframes prizoFloat { 0%,100% { transform: translateY(0) rotate(var(--r,0deg)); } 50% { transform: translateY(-18px) rotate(var(--r,0deg)); } }
  @keyframes prizoFloatSlow { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-26px); } }
  @keyframes prizoSpinSlow { to { transform: rotate(360deg); } }
  @keyframes prizoPulse { 0%,100% { transform: translate(-50%,-50%) scale(1); box-shadow: 0 0 0 0 rgba(105,108,255,.45); } 50% { transform: translate(-50%,-50%) scale(1.04); box-shadow: 0 0 0 18px rgba(105,108,255,0); } }
  @keyframes prizoMarquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
  @keyframes prizoPop { 0% { transform: scale(.6); opacity: 0; } 60% { transform: scale(1.08); } 100% { transform: scale(1); opacity: 1; } }
  @keyframes prizoConfetti { 0% { transform: translateY(-10px) rotate(0); opacity: 1; } 100% { transform: translateY(120px) rotate(320deg); opacity: 0; } }
  .prizo-cta { transition: transform .18s ease, box-shadow .18s ease; }
  .prizo-cta:hover { transform: translateY(-3px) scale(1.02); box-shadow: 0 18px 40px -10px rgba(105,108,255,.6) !important; }
  .prizo-ghost:hover { background: #ECEBFF !important; border-color: #696CFF !important; }
  .prizo-link:hover { color: #696CFF !important; }
  .prizo-game-card { transition: transform .22s ease, box-shadow .22s ease; }
  .prizo-game-card:hover { transform: translateY(-8px); box-shadow: 0 30px 50px -22px rgba(20,17,46,.4) !important; }
  .prizo-price-card.is-pop:hover { transform: translateY(-6px) scale(1.015); }
  .prizo-price-card { transition: transform .2s ease; }
  .prizo-burger { display: none; }
  .prizo-demo-tab:hover { color: #fff !important; }
  .prizo-demo-arrow:hover { background: rgba(255,255,255,.18) !important; }
  @media (max-width: 900px) {
    .prizo-hero-grid { grid-template-columns: 1fr !important; text-align: center; }
    .prizo-hero-cta { justify-content: center !important; }
    .prizo-trust-row { justify-content: center !important; }
    .prizo-h1 { font-size: 52px !important; }
    .prizo-wheel-grid { grid-template-columns: 1fr !important; justify-items: center; text-align: center; gap: 36px !important; }
    .prizo-demo-arrow { display: none !important; }
    .prizo-steps { grid-template-columns: 1fr !important; }
    .prizo-stats { grid-template-columns: 1fr 1fr !important; }
    .prizo-nav-links { display: none !important; }
    .prizo-nav-auth { display: none !important; }
    .prizo-burger { display: inline-flex !important; }
    .prizo-pad { padding-left: 22px !important; padding-right: 22px !important; }
    .prizo-float { display: none !important; }
    .prizo-dash { grid-template-columns: 1fr !important; }
    .prizo-dash-side { display: none !important; }
    .prizo-kpis { grid-template-columns: 1fr 1fr !important; }
    .prizo-dash-grid { grid-template-columns: 1fr !important; }
  }
  @media (max-width: 600px) {
    .prizo-h1 { font-size: 40px !important; line-height: 1.02 !important; letter-spacing: -1.5px !important; }
    .prizo-section-h2 { font-size: 34px !important; }
    .prizo-demo-tab { font-size: 13px !important; padding: 8px 13px !important; }
    .prizo-stage { width: 300px !important; height: 300px !important; }
    .prizo-stats { grid-template-columns: 1fr 1fr !important; gap: 22px !important; }
    .prizo-banner-h2 { font-size: 30px !important; }
    .prizo-final-h2 { font-size: 40px !important; }
    .prizo-stat-value { font-size: 40px !important; }
    .prizo-pad { padding-left: 18px !important; padding-right: 18px !important; }
    .prizo-kpis { grid-template-columns: 1fr 1fr !important; }
  }
`;

function PrizoLogo({ width = 68, height = 45 }: { width?: number; height?: number }) {
  return (
    <img src={prizoLogoColor} alt="Prizo" width={width} height={height} style={{ display: "block" }} />
  );
}

const trustLogos = [
  { src: logo1, cls: "grayscale invert contrast-200 drop-shadow" },
  { src: logo2, cls: "grayscale" },
  { src: logo3, cls: "grayscale" },
  { src: logo4, cls: "grayscale" },
  { src: icecoLogo, cls: "grayscale invert contrast-200 drop-shadow" },
  { src: rinkitLogo, cls: "grayscale" },
];

const steps = [
  { n: "01", icon: "🛒", bg: "#ECEBFF", title: "Install the app", body: "One-click install from the Shopify App Store. No code, no theme edits — Prizo drops right into your post-purchase flow." },
  { n: "02", icon: "🎮", bg: "#FFF3D6", title: "Pick & brand a game", body: "Choose a mini-game, set your prizes and colors. Live preview as you go — match your brand perfectly." },
  { n: "03", icon: "🏆", bg: "#E4FBD3", title: "Reward & re-engage", body: "Winners get instant codes and join your list. You watch repeat orders roll in from the dashboard." },
];

const games = [
  { icon: "🎡", bg: "linear-gradient(135deg,#ECEBFF,#D8D6FF)", title: "Spin the Wheel", body: "The classic spin-the-wheel game. Weighted odds you control, jackpot suspense they can’t resist." },
  { icon: "🎟️", bg: "linear-gradient(135deg,#FFF1D6,#FFE0A8)", title: "Scratch Card", body: "Tap and scratch to reveal a hidden reward. Tactile, satisfying, instantly addictive." },
  { icon: "🔵", bg: "linear-gradient(135deg,#FFE0EC,#FFC2D9)", title: "Plinko", body: "Drop the ball and watch it bounce to a prize. High-suspense, spectator-friendly fun." },
  { icon: "🎁", bg: "linear-gradient(135deg,#D7F7FF,#B3ECFF)", title: "Pick a Box", body: "Pick a box, win what’s inside. Curiosity does the converting for you." },
  { icon: "🕹️", bg: "linear-gradient(135deg,#E4FBD3,#C7F2A8)", title: "Claw Machine", body: "Arcade nostalgia at checkout — steer the claw and grab the reward." },
  { icon: "🎲", bg: "linear-gradient(135deg,#EDE7FF,#D9C9FF)", title: "Lucky Dice", body: "Roll the dice to reveal a reward — a classic chance game shoppers grasp instantly." },
];

const stats = [
  { value: "3.2×", label: "more repeat purchases" },
  { value: "+38%", label: "avg. revenue lift" },
  { value: "87%", label: "shoppers play along" },
  { value: "<2 min", label: "to go live" },
];

const navItems = [
  { icon: "📊", label: "Overview", fg: "#fff", bg: "rgba(105,108,255,.9)" },
  { icon: "🎮", label: "Games", fg: "rgba(255,255,255,.7)", bg: "transparent" },
  { icon: "🎁", label: "Prizes", fg: "rgba(255,255,255,.7)", bg: "transparent" },
  { icon: "📈", label: "Analytics", fg: "rgba(255,255,255,.7)", bg: "transparent" },
  { icon: "👥", label: "Customers", fg: "rgba(255,255,255,.7)", bg: "transparent" },
  { icon: "⚙️", label: "Settings", fg: "rgba(255,255,255,.7)", bg: "transparent" },
];

const kpis = [
  { label: "Total plays", value: "42,180", delta: "▲ 24% vs last mo", deltaColor: "#4E9A1E" },
  { label: "Win rate", value: "63%", delta: "▲ 5 pts", deltaColor: "#4E9A1E" },
  { label: "Revenue from Prizo", value: "$38.4k", delta: "▲ 31%", deltaColor: "#4E9A1E" },
  { label: "Emails captured", value: "9,720", delta: "▲ 18%", deltaColor: "#4E9A1E" },
];

const bars = [
  { h: "38%", color: "#D8D6FF" }, { h: "52%", color: "#D8D6FF" }, { h: "44%", color: "#D8D6FF" },
  { h: "66%", color: "#C2C0FF" }, { h: "58%", color: "#C2C0FF" }, { h: "74%", color: "#9A9CFF" },
  { h: "62%", color: "#C2C0FF" }, { h: "80%", color: "#9A9CFF" }, { h: "70%", color: "#9A9CFF" },
  { h: "88%", color: "#696CFF" }, { h: "78%", color: "#7B7EFF" }, { h: "100%", color: "#696CFF" },
];

const gameRows = [
  { icon: "🎡", bg: "linear-gradient(135deg,#ECEBFF,#D8D6FF)", name: "Spin the Wheel", plays: "24,910 plays", toggleBg: "#696CFF", knobSide: "right" },
  { icon: "🎟️", bg: "linear-gradient(135deg,#FFF1D6,#FFE0A8)", name: "Scratch Card", plays: "12,540 plays", toggleBg: "#696CFF", knobSide: "right" },
  { icon: "🎰", bg: "linear-gradient(135deg,#FFE0EC,#FFC2D9)", name: "Slot Machine", plays: "Paused", toggleBg: "#DAD8E8", knobSide: "left" },
] as const;

const navLinks = [
  { href: "#play", label: "Play demo" },
  { href: "#how", label: "How it works" },
  { href: "#games", label: "Games" },
  { href: "#pricing", label: "Pricing" },
  { href: "#faq", label: "FAQ" },
];

const DEMO_SLIDES = [
  {
    key: "wheel", tab: "Spin the Wheel", tabIcon: "🎡", badge: "▶ Live demo — go ahead",
    title: ["Give the wheel", "a real spin."],
    copy: "This is exactly what your customers see after checkout. Tap the button — land a prize, feel the rush. That little dopamine hit is what brings them back.",
    stats: [
      { value: "3.2×", color: "#FFD43B", label: "more repeat orders" },
      { value: "87%", color: "#B6F36B", label: "choose to play" },
      { value: "+41%", color: "#4ED9E0", label: "email opt-ins" },
    ],
  },
  {
    key: "scratch", tab: "Scratch Card", tabIcon: "🎟️", badge: "✦ Scratch to reveal",
    title: ["Scratch off", "your reward."],
    copy: "Drag across the card to scratch it off — just like the real thing. A tactile, satisfying reveal shoppers can't help but finish.",
    stats: [
      { value: "2.9×", color: "#FFD43B", label: "more repeat orders" },
      { value: "91%", color: "#B6F36B", label: "scratch to the end" },
      { value: "+37%", color: "#4ED9E0", label: "email opt-ins" },
    ],
  },
  {
    key: "box", tab: "Mystery Box", tabIcon: "🎁", badge: "🎁 Pick a box",
    title: ["Pick a box,", "win a surprise."],
    copy: "Curiosity converts. Shoppers choose one of three boxes and unwrap an instant prize — the suspense is half the fun.",
    stats: [
      { value: "3.5×", color: "#FFD43B", label: "more repeat orders" },
      { value: "84%", color: "#B6F36B", label: "open a box" },
      { value: "+44%", color: "#4ED9E0", label: "email opt-ins" },
    ],
  },
] as const;

const demoArrowStyle = (side: "left" | "right"): React.CSSProperties => ({
  position: "absolute", top: "38%", [side]: -6, transform: "translateY(-50%)", zIndex: 8,
  width: 44, height: 44, borderRadius: "50%", border: "1px solid rgba(255,255,255,.18)",
  background: "rgba(255,255,255,.08)", backdropFilter: "blur(6px)", color: "#fff",
  fontSize: 26, lineHeight: 1, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center",
});

// The Pixi (WebGL) game scenes are client-only and lazy-loaded so they never
// touch the SSR bundle and don't weigh down the rest of the landing page.
const DemoGame = lazy(() => import("../components/landing-games"));

type GameKey = "wheel" | "scratch" | "box";

function DemoSkeleton({ game }: { game: GameKey }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 26 }}>
      <div style={{ width: game === "box" ? 340 : 320, height: game === "box" ? 300 : 320, maxWidth: "100%", borderRadius: 24, background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.08)" }} />
      <div style={{ height: 30 }} />
    </div>
  );
}

// Only the active slide mounts a live WebGL context; switching away tears it
// down, so at most one Pixi scene runs at a time.
function GameSlot({ game, active }: { game: GameKey; active: boolean }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!active || !mounted) return <DemoSkeleton game={game} />;
  return <Suspense fallback={<DemoSkeleton game={game} />}><DemoGame game={game} /></Suspense>;
}

export function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [activeDemo, setActiveDemo] = useState(0);

  return (
    <div className="prizo-root" style={{ overflowX: "hidden" }}>
      <style dangerouslySetInnerHTML={{ __html: PAGE_CSS }} />

      {/* Structured data: brand entity, the app, and the FAQ — for rich
          results and so AI search engines can understand and cite Prizo. */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(getOrganizationJsonLd()) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(getAppWithReviewsJsonLd()) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(getFaqPageJsonLd([...LANDING_FAQ], "/")) }}
      />

      {/* NAV */}
      <nav style={{ position: "sticky", top: 0, zIndex: 50, backdropFilter: "blur(14px)", background: "rgba(251,250,255,.82)", borderBottom: "1px solid rgba(105,108,255,.12)" }}>
        <div className="prizo-pad" style={{ maxWidth: 1200, margin: "0 auto", padding: "16px 32px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Link to="/" style={{ display: "flex", alignItems: "center", textDecoration: "none" }} aria-label="Prizo home">
            <PrizoLogo width={68} height={45} />
          </Link>
          <div className="prizo-nav-links" style={{ display: "flex", alignItems: "center", gap: 34 }}>
            {navLinks.map((l) => (
              <a key={l.href} href={l.href} className="prizo-link" style={{ textDecoration: "none", color: "#4A4763", fontWeight: 600, fontSize: 15, transition: "color .15s" }}>{l.label}</a>
            ))}
          </div>
          <div className="prizo-nav-auth" style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <Link to="/login" className="prizo-link" style={{ textDecoration: "none", color: "#14112E", fontWeight: 700, fontSize: 15 }}>Login</Link>
            <Link to="/register" className="prizo-cta" style={{ textDecoration: "none", background: "#14112E", color: "#fff", fontWeight: 700, fontSize: 15, padding: "11px 20px", borderRadius: 12, boxShadow: "0 10px 24px -10px rgba(20,17,46,.5)" }}>Get started</Link>
          </div>
          {/* Mobile burger */}
          <button
            aria-label="Menu"
            onClick={() => setMobileMenuOpen((v) => !v)}
            className="prizo-burger"
            style={{ alignItems: "center", justifyContent: "center", width: 44, height: 44, border: "none", background: "transparent", cursor: "pointer", flexDirection: "column", gap: 5 }}
          >
            <span style={{ width: 22, height: 2, background: "#14112E", borderRadius: 2, transition: "transform .25s", transform: mobileMenuOpen ? "translateY(7px) rotate(45deg)" : "none" }} />
            <span style={{ width: 22, height: 2, background: "#14112E", borderRadius: 2, opacity: mobileMenuOpen ? 0 : 1, transition: "opacity .2s" }} />
            <span style={{ width: 22, height: 2, background: "#14112E", borderRadius: 2, transition: "transform .25s", transform: mobileMenuOpen ? "translateY(-7px) rotate(-45deg)" : "none" }} />
          </button>
        </div>

        {/* Mobile menu drawer */}
        {mobileMenuOpen && (
          <div className="prizo-pad" style={{ padding: "8px 22px 22px", borderTop: "1px solid rgba(105,108,255,.12)", display: "flex", flexDirection: "column", gap: 4 }}>
            {navLinks.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setMobileMenuOpen(false)} style={{ textDecoration: "none", color: "#4A4763", fontWeight: 600, fontSize: 16, padding: "12px 6px" }}>{l.label}</a>
            ))}
            <Link to="/login" onClick={() => setMobileMenuOpen(false)} style={{ textDecoration: "none", color: "#14112E", fontWeight: 700, fontSize: 16, padding: "12px 6px" }}>Login</Link>
            <Link to="/register" onClick={() => setMobileMenuOpen(false)} style={{ textDecoration: "none", textAlign: "center", background: "#14112E", color: "#fff", fontWeight: 700, fontSize: 16, padding: "13px 20px", borderRadius: 12, marginTop: 6 }}>Get started</Link>
          </div>
        )}
      </nav>

      {/* HERO */}
      <header className="prizo-pad" style={{ position: "relative", maxWidth: 1200, margin: "0 auto", padding: "70px 32px 40px" }}>
        <div className="prizo-float" style={{ position: "absolute", top: 36, right: "36%", "--r": "-12deg", animation: "prizoFloat 5s ease-in-out infinite", zIndex: 4 } as React.CSSProperties}>
          <div style={{ background: "#FFD43B", color: "#4A3a00", fontWeight: 800, fontSize: 14, padding: "10px 16px", borderRadius: 14, boxShadow: "0 14px 30px -10px rgba(255,180,0,.6)", border: "2px solid #fff" }}>🎁 +20% OFF</div>
        </div>
        <div className="prizo-float" style={{ position: "absolute", bottom: 30, right: "38%", "--r": "8deg", animation: "prizoFloat 6.5s ease-in-out infinite .6s", zIndex: 4 } as React.CSSProperties}>
          <div style={{ background: "#B6F36B", color: "#213a00", fontWeight: 800, fontSize: 14, padding: "10px 16px", borderRadius: 14, boxShadow: "0 14px 30px -10px rgba(120,200,40,.55)", border: "2px solid #fff" }}>FREE SHIP ✦</div>
        </div>
        <div className="prizo-float" style={{ position: "absolute", top: 130, right: "1%", "--r": "10deg", animation: "prizoFloat 5.6s ease-in-out infinite .3s", zIndex: 4 } as React.CSSProperties}>
          <div style={{ background: "#FF8FB1", color: "#5a0024", fontWeight: 800, fontSize: 14, padding: "10px 16px", borderRadius: 14, boxShadow: "0 14px 30px -10px rgba(255,90,140,.5)", border: "2px solid #fff" }}>WIN 🏆</div>
        </div>

        <div className="prizo-hero-grid" style={{ position: "relative", zIndex: 2, display: "grid", gridTemplateColumns: "1.05fr .95fr", gap: 50, alignItems: "center" }}>
          {/* left */}
          <div>
            <h1 className="prizo-h1" style={{ fontFamily: "'Bricolage Grotesque'", fontWeight: 800, fontSize: 62, lineHeight: "1.0", letterSpacing: "-2px", marginBottom: 22 }}>
              The Shopify gamification<br />app that turns every<br />purchase into a{" "}
              <span style={{ position: "relative", display: "inline-block", color: "#696CFF" }}>
                game
                <span style={{ position: "absolute", left: 0, right: 0, bottom: 4, height: 14, background: "#FFD43B", zIndex: -1, borderRadius: 6 }} />
              </span>.
            </h1>
            <p style={{ fontSize: 19, lineHeight: 1.5, color: "#4A4763", maxWidth: 480, marginBottom: 32 }}>
              <strong style={{ color: "#14112E" }}>Turn every purchase into a game.</strong> Prizo is the <strong style={{ color: "#14112E" }}>spin the wheel &amp; gamification app for Shopify</strong> that surprises shoppers with a fun mini-game right after checkout. Reward winners with instant discounts and watch repeat sales climb — <strong style={{ color: "#14112E" }}>zero extra work.</strong>
            </p>
            <div className="prizo-hero-cta" style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 30 }}>
              <a href={SHOPIFY_APP_URL} target="_blank" rel="noopener noreferrer" className="prizo-cta" style={{ textDecoration: "none", background: "linear-gradient(135deg,#696CFF,#8A6CFF)", color: "#fff", fontWeight: 800, fontSize: 17, padding: "17px 30px", borderRadius: 15, boxShadow: "0 16px 34px -12px rgba(105,108,255,.65)" }}>Try it free →</a>
              <button onClick={() => setVideoModalOpen(true)} className="prizo-ghost" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10, background: "#fff", border: "2px solid #E4E2F5", color: "#14112E", fontWeight: 700, fontSize: 16, padding: "15px 24px", borderRadius: 15, transition: "background .15s, border-color .15s", fontFamily: "'Plus Jakarta Sans'" }}>
                <span style={{ width: 30, height: 30, borderRadius: "50%", background: "#ECEBFF", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#696CFF" }}>▶</span>
                See it in action
              </button>
            </div>
            <div className="prizo-trust-row" style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ display: "flex" }}>
                <div style={{ width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg,#FFD43B,#FF9F40)", border: "2.5px solid #FBFAFF" }} />
                <div style={{ width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg,#696CFF,#9A7BFF)", border: "2.5px solid #FBFAFF", marginLeft: -12 }} />
                <div style={{ width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg,#B6F36B,#4ED9E0)", border: "2.5px solid #FBFAFF", marginLeft: -12 }} />
                <div style={{ width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg,#FF8FB1,#FF6B6B)", border: "2.5px solid #FBFAFF", marginLeft: -12 }} />
              </div>
              <div style={{ fontSize: 14, color: "#4A4763", lineHeight: 1.3 }}><strong style={{ color: "#14112E" }}>500,000+ games played</strong><br />and counting</div>
            </div>
            {/* Visible rating — matches the aggregateRating in structured data */}
            <a
              href={SHOPIFY_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="prizo-link"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 16, textDecoration: "none", color: "#4A4763", fontSize: 14, fontWeight: 600 }}
            >
              <span style={{ color: "#FFB400", fontSize: 16, letterSpacing: 1 }}>★★★★★</span>
              <span><strong style={{ color: "#14112E" }}>5.0</strong> · 6 reviews on the Shopify App Store</span>
            </a>
          </div>

          {/* right: phone mockup */}
          <div style={{ position: "relative", display: "flex", justifyContent: "center", animation: "prizoFloatSlow 7s ease-in-out infinite" }}>
            <div style={{ position: "absolute", width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle, rgba(105,108,255,.35), transparent 65%)", top: 40, filter: "blur(10px)" }} />
            <div style={{ position: "absolute", top: 20, left: 30, width: 12, height: 12, background: "#FFD43B", borderRadius: 3, animation: "prizoConfetti 2.6s ease-in infinite", zIndex: 5 }} />
            <div style={{ position: "absolute", top: 10, right: 50, width: 10, height: 10, background: "#B6F36B", borderRadius: "50%", animation: "prizoConfetti 3.1s ease-in infinite .5s", zIndex: 5 }} />
            <div style={{ position: "absolute", top: 30, right: 90, width: 9, height: 14, background: "#FF8FB1", borderRadius: 3, animation: "prizoConfetti 2.9s ease-in infinite 1s", zIndex: 5 }} />

            <div style={{ position: "relative", zIndex: 3, width: 300, height: 600, background: "#14112E", borderRadius: 44, padding: 12, boxShadow: "0 40px 80px -24px rgba(20,17,46,.55)", border: "1px solid rgba(255,255,255,.08)" }}>
              <div style={{ position: "absolute", top: 24, left: "50%", transform: "translateX(-50%)", width: 110, height: 26, background: "#14112E", borderRadius: 999, zIndex: 4 }} />
              <div style={{ width: "100%", height: "100%", borderRadius: 34, background: "linear-gradient(165deg,#5B5EF0,#7C5BFF 55%,#9A6CFF)", overflow: "hidden", position: "relative", display: "flex", flexDirection: "column", alignItems: "center", padding: "70px 22px 26px" }}>
                <div style={{ color: "rgba(255,255,255,.85)", fontWeight: 700, fontSize: 13, letterSpacing: ".5px", textTransform: "uppercase", marginBottom: 6 }}>Order confirmed 🎉</div>
                <div style={{ color: "#fff", fontFamily: "'Bricolage Grotesque'", fontWeight: 800, fontSize: 26, textAlign: "center", lineHeight: 1.05, marginBottom: 18 }}>Spin to win<br />a reward!</div>
                <div style={{ position: "relative", width: 200, height: 200, marginBottom: 20 }}>
                  <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "8px solid #fff", boxShadow: "0 16px 30px -12px rgba(0,0,0,.5)", animation: "prizoSpinSlow 9s linear infinite", background: "conic-gradient(#FFD43B 0 45deg,#FF8FB1 45deg 90deg,#4ED9E0 90deg 135deg,#B6F36B 135deg 180deg,#FFD43B 180deg 225deg,#FF8FB1 225deg 270deg,#4ED9E0 270deg 315deg,#B6F36B 315deg 360deg)" }} />
                  <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 46, height: 46, borderRadius: "50%", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, color: "#696CFF", fontSize: 13, boxShadow: "0 4px 10px rgba(0,0,0,.2)" }}>SPIN</div>
                  <div style={{ position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)", width: 0, height: 0, borderLeft: "11px solid transparent", borderRight: "11px solid transparent", borderTop: "18px solid #14112E" }} />
                </div>
                <div style={{ background: "rgba(255,255,255,.16)", backdropFilter: "blur(6px)", borderRadius: 14, padding: "12px 16px", textAlign: "center", width: "100%" }}>
                  <div style={{ color: "rgba(255,255,255,.8)", fontSize: 12, fontWeight: 600 }}>You could win up to</div>
                  <div style={{ color: "#fff", fontFamily: "'Bricolage Grotesque'", fontWeight: 800, fontSize: 22 }}>25% OFF</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* TRUST MARQUEE — real merchant logos */}
      <section style={{ padding: "28px 0 50px" }}>
        <div className="prizo-pad" style={{ textAlign: "center", color: "#8A87A3", fontWeight: 600, fontSize: 13.5, letterSpacing: ".6px", textTransform: "uppercase", marginBottom: 24 }}>Chosen by merchants who put retention first</div>
        <div style={{ position: "relative", overflow: "hidden", maskImage: "linear-gradient(90deg,transparent,#000 12%,#000 88%,transparent)", WebkitMaskImage: "linear-gradient(90deg,transparent,#000 12%,#000 88%,transparent)" }}>
          <div style={{ display: "flex", gap: 64, width: "max-content", animation: "prizoMarquee 26s linear infinite", alignItems: "center", padding: "0 32px" }}>
            {[...trustLogos, ...trustLogos].map((l, i) => (
              <img key={i} src={l.src} alt={i % trustLogos.length === 0 ? "Merchant logo" : ""} className={`filter ${l.cls} opacity-70`} style={{ height: 36, objectFit: "contain" }} />
            ))}
          </div>
        </div>
      </section>

      {/* PLAYABLE DEMOS SLIDER */}
      <section id="play" style={{ position: "relative", background: "#14112E", color: "#fff", padding: "84px 0 90px", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -120, left: -80, width: 380, height: 380, borderRadius: "50%", background: "radial-gradient(circle, rgba(105,108,255,.4), transparent 70%)" }} />
        <div style={{ position: "absolute", bottom: -140, right: -60, width: 420, height: 420, borderRadius: "50%", background: "radial-gradient(circle, rgba(154,108,255,.28), transparent 70%)" }} />

        <div className="prizo-pad" style={{ position: "relative", maxWidth: 1100, margin: "0 auto", padding: "0 32px" }}>
          {/* tab bar */}
          <div className="prizo-demo-tabs" style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginBottom: 44 }}>
            {DEMO_SLIDES.map((s, i) => (
              <button
                key={s.key}
                onClick={() => setActiveDemo(i)}
                className="prizo-demo-tab"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer",
                  fontFamily: "'Plus Jakarta Sans'", fontWeight: 700, fontSize: 14.5, padding: "10px 18px", borderRadius: 999,
                  border: i === activeDemo ? "1px solid transparent" : "1px solid rgba(255,255,255,.16)",
                  background: i === activeDemo ? "linear-gradient(135deg,#696CFF,#8A6CFF)" : "rgba(255,255,255,.05)",
                  color: i === activeDemo ? "#fff" : "rgba(255,255,255,.7)",
                  boxShadow: i === activeDemo ? "0 12px 26px -12px rgba(105,108,255,.7)" : "none",
                  transition: "background .2s, color .2s, border-color .2s",
                }}
              >
                <span style={{ fontSize: 16 }}>{s.tabIcon}</span>{s.tab}
              </button>
            ))}
          </div>

          {/* slider viewport */}
          <div style={{ position: "relative" }}>
            <div style={{ overflow: "hidden" }}>
              <div style={{ display: "flex", transition: "transform .55s cubic-bezier(.16,1,.3,1)", transform: `translateX(-${activeDemo * 100}%)` }}>
                {DEMO_SLIDES.map((s, i) => (
                  <div key={s.key} aria-hidden={i !== activeDemo} style={{ minWidth: "100%", padding: "4px" }}>
                    <div className="prizo-wheel-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, alignItems: "center" }}>
                      <div>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,.1)", color: "#C9C7FF", fontWeight: 700, fontSize: 13, padding: "7px 14px", borderRadius: 999, marginBottom: 22 }}>{s.badge}</div>
                        <h2 className="prizo-section-h2" style={{ fontFamily: "'Bricolage Grotesque'", fontWeight: 800, fontSize: 52, lineHeight: 1, letterSpacing: "-1.5px", marginBottom: 18 }}>{s.title[0]}<br />{s.title[1]}</h2>
                        <p style={{ fontSize: 18, color: "rgba(255,255,255,.7)", lineHeight: 1.5, maxWidth: 420, marginBottom: 28 }}>{s.copy}</p>
                        <div style={{ display: "flex", gap: 26, flexWrap: "wrap" }}>
                          {s.stats.map((st, k) => (
                            <div key={k}>
                              <div style={{ fontFamily: "'Bricolage Grotesque'", fontWeight: 800, fontSize: 34, color: st.color }}>{st.value}</div>
                              <div style={{ fontSize: 13.5, color: "rgba(255,255,255,.6)" }}>{st.label}</div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div style={{ display: "flex", justifyContent: "center" }}>
                        <GameSlot game={s.key} active={i === activeDemo} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* arrows */}
            <button aria-label="Previous demo" onClick={() => setActiveDemo((v) => (v + DEMO_SLIDES.length - 1) % DEMO_SLIDES.length)} className="prizo-demo-arrow prizo-demo-arrow-left" style={demoArrowStyle("left")}>‹</button>
            <button aria-label="Next demo" onClick={() => setActiveDemo((v) => (v + 1) % DEMO_SLIDES.length)} className="prizo-demo-arrow prizo-demo-arrow-right" style={demoArrowStyle("right")}>›</button>
          </div>

          {/* dots */}
          <div style={{ display: "flex", justifyContent: "center", gap: 9, marginTop: 30 }}>
            {DEMO_SLIDES.map((s, i) => (
              <button
                key={s.key}
                aria-label={`Go to ${s.tab}`}
                onClick={() => setActiveDemo(i)}
                style={{ width: i === activeDemo ? 28 : 9, height: 9, borderRadius: 999, border: "none", cursor: "pointer", padding: 0, background: i === activeDemo ? "#696CFF" : "rgba(255,255,255,.25)", transition: "width .25s, background .25s" }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="prizo-pad" style={{ maxWidth: 1200, margin: "0 auto", padding: "96px 32px" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <div style={{ display: "inline-block", color: "#696CFF", fontWeight: 800, fontSize: 14, letterSpacing: 1, textTransform: "uppercase", marginBottom: 14 }}>How Prizo works on Shopify</div>
          <h2 className="prizo-section-h2" style={{ fontFamily: "'Bricolage Grotesque'", fontWeight: 800, fontSize: 54, letterSpacing: "-1.5px", lineHeight: 1 }}>Live in minutes,<br />not a sprint.</h2>
        </div>
        <div className="prizo-steps" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24 }}>
          {steps.map((step) => (
            <div key={step.n} style={{ position: "relative", background: "#fff", border: "1px solid #EDEBF7", borderRadius: 24, padding: "34px 28px", boxShadow: "0 20px 40px -28px rgba(20,17,46,.3)" }}>
              <div style={{ position: "absolute", top: -18, left: 28, width: 48, height: 48, borderRadius: 14, background: step.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, boxShadow: "0 10px 22px -8px rgba(20,17,46,.3)", border: "2px solid #fff" }}>{step.icon}</div>
              <div style={{ fontFamily: "'Bricolage Grotesque'", fontWeight: 800, fontSize: 15, color: "#B7B4CC", margin: "18px 0 10px" }}>LEVEL {step.n}</div>
              <h3 style={{ fontFamily: "'Bricolage Grotesque'", fontWeight: 700, fontSize: 24, letterSpacing: "-.5px", marginBottom: 10 }}>{step.title}</h3>
              <p style={{ fontSize: 15.5, lineHeight: 1.55, color: "#5C5976" }}>{step.body}</p>
            </div>
          ))}
        </div>

        {/* video */}
        <div style={{ marginTop: 56, textAlign: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 9, background: "#ECEBFF", color: "#4F52D6", fontWeight: 700, fontSize: 13.5, padding: "8px 15px", borderRadius: 999, marginBottom: 22 }}>▶ Watch the full walkthrough</div>
          <div style={{ position: "relative", maxWidth: 880, margin: "0 auto", borderRadius: 22, overflow: "hidden", boxShadow: "0 40px 80px -36px rgba(20,17,46,.5)", border: "1px solid #E7E5F5", background: "#14112E" }}>
            <div style={{ position: "relative", width: "100%", paddingBottom: "56.25%", height: 0 }}>
              <iframe src="https://www.youtube.com/embed/v_7dV9c6GdE" title="Prizo walkthrough" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }} />
            </div>
          </div>
        </div>
      </section>

      {/* GAMES GALLERY */}
      <section id="games" style={{ background: "linear-gradient(180deg,#F3F1FF,#FBFAFF)", padding: "96px 0" }}>
        <div className="prizo-pad" style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px" }}>
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 20, marginBottom: 50 }}>
            <div>
              <div style={{ color: "#696CFF", fontWeight: 800, fontSize: 14, letterSpacing: 1, textTransform: "uppercase", marginBottom: 14 }}>Nine gamification games in one app</div>
              <h2 className="prizo-section-h2" style={{ fontFamily: "'Bricolage Grotesque'", fontWeight: 800, fontSize: 54, letterSpacing: "-1.5px", lineHeight: 1 }}>One app.<br />A whole arcade.</h2>
            </div>
            <p style={{ maxWidth: 360, fontSize: 16, color: "#5C5976", lineHeight: 1.55 }}>From spin the wheel and scratch cards to Plinko and pick-a-box, swap games anytime to keep things fresh. Every game is mobile-first, on-brand, and runs as a post-purchase reward or popup.</p>
          </div>
          <div className="prizo-steps" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24 }}>
            {games.map((game) => (
              <div key={game.title} className="prizo-game-card" style={{ background: "#fff", border: "1px solid #EDEBF7", borderRadius: 24, padding: 30, boxShadow: "0 16px 36px -26px rgba(20,17,46,.35)" }}>
                <div style={{ width: 70, height: 70, borderRadius: 20, background: game.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 34, marginBottom: 22 }}>{game.icon}</div>
                <h3 style={{ fontFamily: "'Bricolage Grotesque'", fontWeight: 700, fontSize: 23, letterSpacing: "-.5px", marginBottom: 8 }}>{game.title}</h3>
                <p style={{ fontSize: 15, lineHeight: 1.55, color: "#5C5976" }}>{game.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DASHBOARD */}
      <section id="dashboard" style={{ padding: "96px 0 70px" }}>
        <div className="prizo-pad" style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px" }}>
          <div style={{ textAlign: "center", maxWidth: 640, margin: "0 auto 50px" }}>
            <div style={{ color: "#696CFF", fontWeight: 800, fontSize: 14, letterSpacing: 1, textTransform: "uppercase", marginBottom: 14 }}>Your gamification dashboard</div>
            <h2 className="prizo-section-h2" style={{ fontFamily: "'Bricolage Grotesque'", fontWeight: 800, fontSize: 54, letterSpacing: "-1.5px", lineHeight: 1, marginBottom: 18 }}>See everything.<br />Control everything.</h2>
            <p style={{ fontSize: 18, color: "#5C5976", lineHeight: 1.55 }}>Track plays, wins, revenue and emails in real time — then switch games, tune prizes and odds in a couple of clicks. No dev needed.</p>
          </div>

          <div style={{ background: "#fff", border: "1px solid #E7E5F5", borderRadius: 22, boxShadow: "0 50px 90px -40px rgba(20,17,46,.45)", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", background: "#F4F2FB", borderBottom: "1px solid #E7E5F5" }}>
              <div style={{ display: "flex", gap: 7 }}>
                <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#FF6B6B" }} />
                <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#FFD43B" }} />
                <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#B6F36B" }} />
              </div>
              <div style={{ flex: 1, maxWidth: 320, margin: "0 auto", background: "#fff", border: "1px solid #E7E5F5", borderRadius: 8, padding: "6px 14px", fontSize: 12.5, color: "#8A87A3", textAlign: "center" }}>🔒 app.prizo.io/dashboard</div>
            </div>

            <div className="prizo-dash" style={{ display: "grid", gridTemplateColumns: "210px 1fr" }}>
              <aside className="prizo-dash-side" style={{ background: "#14112E", padding: "22px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 8px 18px" }}>
                  <span style={{ width: 26, height: 26, borderRadius: 8, background: "linear-gradient(135deg,#696CFF,#9A7BFF)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><span style={{ width: 10, height: 10, borderRadius: "50%", border: "2.5px solid #fff" }} /></span>
                  <span style={{ color: "#fff", fontFamily: "'Bricolage Grotesque'", fontWeight: 800, fontSize: 17 }}>Prizo</span>
                </div>
                {navItems.map((item) => (
                  <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 11, padding: "11px 12px", borderRadius: 10, fontSize: 14, fontWeight: 600, color: item.fg, background: item.bg }}>
                    <span style={{ fontSize: 16 }}>{item.icon}</span>{item.label}
                  </div>
                ))}
                <div style={{ marginTop: "auto", background: "rgba(255,255,255,.06)", borderRadius: 12, padding: 14 }}>
                  <div style={{ color: "#fff", fontWeight: 700, fontSize: 13, marginBottom: 3 }}>Growth plan</div>
                  <div style={{ color: "rgba(255,255,255,.5)", fontSize: 11.5 }}>Unlimited plays active</div>
                </div>
              </aside>

              <div style={{ padding: "26px 28px", background: "#FBFAFF" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 22 }}>
                  <div>
                    <div style={{ fontFamily: "'Bricolage Grotesque'", fontWeight: 800, fontSize: 24, letterSpacing: "-.5px" }}>Good morning, Nova 👋</div>
                    <div style={{ fontSize: 13.5, color: "#8A87A3" }}>Here&apos;s how your games performed</div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <div style={{ background: "#fff", border: "1px solid #E7E5F5", borderRadius: 10, padding: "9px 14px", fontSize: 13, fontWeight: 600, color: "#5C5976" }}>Last 30 days ▾</div>
                    <div style={{ background: "#696CFF", color: "#fff", borderRadius: 10, padding: "9px 16px", fontSize: 13, fontWeight: 700 }}>+ New game</div>
                  </div>
                </div>

                <div className="prizo-kpis" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 18 }}>
                  {kpis.map((kpi) => (
                    <div key={kpi.label} style={{ background: "#fff", border: "1px solid #EDEBF7", borderRadius: 16, padding: 16 }}>
                      <div style={{ fontSize: 12.5, color: "#8A87A3", fontWeight: 600, marginBottom: 8 }}>{kpi.label}</div>
                      <div style={{ fontFamily: "'Bricolage Grotesque'", fontWeight: 800, fontSize: 26, letterSpacing: "-.5px" }}>{kpi.value}</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: kpi.deltaColor, marginTop: 4 }}>{kpi.delta}</div>
                    </div>
                  ))}
                </div>

                <div className="prizo-dash-grid" style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 14 }}>
                  <div style={{ background: "#fff", border: "1px solid #EDEBF7", borderRadius: 16, padding: 18 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
                      <div style={{ fontWeight: 700, fontSize: 14.5 }}>Plays this month</div>
                      <div style={{ fontSize: 12, background: "rgba(120,200,40,.12)", padding: "4px 10px", borderRadius: 999, fontWeight: 700, color: "#4E9A1E" }}>▲ 24%</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 130 }}>
                      {bars.map((bar, i) => (
                        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end", height: "100%" }}>
                          <div style={{ width: "100%", height: bar.h, background: bar.color, borderRadius: "5px 5px 3px 3px" }} />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ background: "#fff", border: "1px solid #EDEBF7", borderRadius: 16, padding: 18 }}>
                    <div style={{ fontWeight: 700, fontSize: 14.5, marginBottom: 14 }}>Active games</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {gameRows.map((row) => (
                        <div key={row.name} style={{ display: "flex", alignItems: "center", gap: 11 }}>
                          <span style={{ width: 34, height: 34, borderRadius: 10, background: row.bg, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 17 }}>{row.icon}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13.5, fontWeight: 700 }}>{row.name}</div>
                            <div style={{ fontSize: 11.5, color: "#8A87A3" }}>{row.plays}</div>
                          </div>
                          <span style={{ width: 38, height: 22, borderRadius: 999, background: row.toggleBg, position: "relative", flexShrink: 0 }}>
                            <span style={{ position: "absolute", top: 3, [row.knobSide]: 3, width: 16, height: 16, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,.25)" }} />
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BANNER */}
      <section className="prizo-pad" style={{ maxWidth: 1200, margin: "0 auto", padding: "30px 32px 96px" }}>
        <div style={{ background: "linear-gradient(135deg,#696CFF,#8A6CFF)", borderRadius: 32, padding: "56px 40px", textAlign: "center", boxShadow: "0 40px 70px -30px rgba(105,108,255,.6)", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -40, right: -20, fontSize: 160, opacity: 0.12 }}>🎯</div>
          <h2 className="prizo-banner-h2" style={{ color: "#fff", fontFamily: "'Bricolage Grotesque'", fontWeight: 800, fontSize: 40, letterSpacing: "-1px", marginBottom: 40 }}>Numbers our merchants love.</h2>
          <div className="prizo-stats" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 30 }}>
            {stats.map((stat) => (
              <div key={stat.label}>
                <div className="prizo-stat-value" style={{ fontFamily: "'Bricolage Grotesque'", fontWeight: 800, fontSize: 52, color: "#fff", lineHeight: 1, letterSpacing: "-1.5px" }}>{stat.value}</div>
                <div style={{ color: "rgba(255,255,255,.82)", fontSize: 15, fontWeight: 600, marginTop: 8 }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING — reuse wired <Plans /> */}
      <section id="pricing" className="prizo-pad" style={{ maxWidth: 1100, margin: "0 auto", padding: "0 32px 96px" }}>
        <div style={{ textAlign: "center", marginBottom: 54 }}>
          <div style={{ color: "#696CFF", fontWeight: 800, fontSize: 14, letterSpacing: 1, textTransform: "uppercase", marginBottom: 14 }}>Simple pricing</div>
          <h2 className="prizo-section-h2" style={{ fontFamily: "'Bricolage Grotesque'", fontWeight: 800, fontSize: 54, letterSpacing: "-1.5px", lineHeight: 1 }}>Start free.<br />Scale when you win.</h2>
        </div>
        <Plans fontClass={plansFontClass} />
      </section>

      {/* FINAL CTA */}
      <section className="prizo-pad" style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px 90px" }}>
        <div style={{ position: "relative", background: "#14112E", borderRadius: 36, padding: "80px 40px", textAlign: "center", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -80, left: -40, width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(105,108,255,.5), transparent 70%)" }} />
          <div style={{ position: "absolute", bottom: -90, right: -30, width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,212,59,.25), transparent 70%)" }} />
          <div style={{ position: "relative", zIndex: 2 }}>
            <div style={{ fontSize: 46, marginBottom: 14 }}>🎡</div>
            <h2 className="prizo-final-h2" style={{ color: "#fff", fontFamily: "'Bricolage Grotesque'", fontWeight: 800, fontSize: 60, letterSpacing: "-2px", lineHeight: 1, marginBottom: 20 }}>Ready to make<br />checkout fun?</h2>
            <p style={{ color: "rgba(255,255,255,.7)", fontSize: 19, maxWidth: 480, margin: "0 auto 34px" }}>Install Prizo on Shopify, pick a game, and start turning one-time buyers into regulars today.</p>
            <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
              <a href={SHOPIFY_APP_URL} target="_blank" rel="noopener noreferrer" className="prizo-cta" style={{ textDecoration: "none", background: "linear-gradient(135deg,#FFD43B,#FF9F40)", color: "#4A3000", fontWeight: 800, fontSize: 18, padding: "17px 34px", borderRadius: 16, boxShadow: "0 16px 34px -12px rgba(255,180,0,.6)" }}>Add to Shopify — free →</a>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="prizo-ghost" style={{ textDecoration: "none", background: "rgba(255,255,255,.08)", border: "2px solid rgba(255,255,255,.18)", color: "#fff", fontWeight: 700, fontSize: 17, padding: "15px 28px", borderRadius: 16 }}>Book a demo</a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ — crawlable, keyword-rich answers + FAQPage structured data */}
      <section id="faq" className="prizo-pad" style={{ maxWidth: 820, margin: "0 auto", padding: "0 32px 90px" }}>
        <div style={{ textAlign: "center", marginBottom: 44 }}>
          <div style={{ color: "#696CFF", fontWeight: 800, fontSize: 14, letterSpacing: 1, textTransform: "uppercase", marginBottom: 14 }}>FAQ</div>
          <h2 className="prizo-section-h2" style={{ fontFamily: "'Bricolage Grotesque'", fontWeight: 800, fontSize: 48, letterSpacing: "-1.5px", lineHeight: 1 }}>Questions, answered.</h2>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {LANDING_FAQ.map((item) => (
            <div key={item.question} style={{ background: "#fff", border: "1px solid #EDEBF7", borderRadius: 18, padding: "24px 26px", boxShadow: "0 16px 36px -28px rgba(20,17,46,.3)" }}>
              <h3 style={{ fontFamily: "'Bricolage Grotesque'", fontWeight: 700, fontSize: 19, letterSpacing: "-.3px", color: "#14112E", marginBottom: 10 }}>{item.question}</h3>
              <p style={{ fontSize: 15.5, lineHeight: 1.6, color: "#5C5976" }}>{item.answer}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED / PRIORITY INTERNAL LINKS — contextual, descriptive in-body
          links from the homepage to the highest-value pages. Unlike the flat
          link map below (every route, generic anchor), this concentrates
          internal equity on tier-1 priority pages with intent-rich anchors. */}
      <section
        aria-label="Popular guides and comparisons"
        className="prizo-pad"
        style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px 70px" }}
      >
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ color: "#696CFF", fontWeight: 800, fontSize: 14, letterSpacing: 1, textTransform: "uppercase", marginBottom: 14 }}>Guides &amp; comparisons</div>
          <h2 className="prizo-section-h2" style={{ fontFamily: "'Bricolage Grotesque'", fontWeight: 800, fontSize: 40, letterSpacing: "-1.2px", lineHeight: 1.05 }}>Popular on Prizo</h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 18 }}>
          {[
            { to: "/solutions/reduce-cart-abandonment", anchor: "How to reduce cart abandonment", blurb: "Win back hesitant shoppers with a game before they leave checkout." },
            { to: "/solutions/increase-aov", anchor: "Increase average order value", blurb: "Nudge bigger baskets with spend-to-unlock rewards and thresholds." },
            { to: "/compare/wheelio", anchor: "Prizo vs Wheelio", blurb: "How Prizo compares to Wheelio for Shopify spin-to-win." },
            { to: "/compare/spin-wheel-apps", anchor: "Best spin-the-wheel apps for Shopify", blurb: "The top spin-the-wheel apps, compared side by side." },
            { to: "/seo/spin-the-wheel", anchor: "Add a spin-the-wheel game", blurb: "Set up the classic prize wheel on your store in minutes." },
            { to: "/compare/privy", anchor: "Prizo vs Privy", blurb: "Gamified games and discounts versus Privy popups." },
          ].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="prizo-link"
              style={{ display: "block", textDecoration: "none", background: "#fff", border: "1px solid #EDEBF7", borderRadius: 18, padding: "22px 24px", boxShadow: "0 16px 36px -28px rgba(20,17,46,.3)" }}
            >
              <h3 style={{ fontFamily: "'Bricolage Grotesque'", fontWeight: 700, fontSize: 18, letterSpacing: "-.3px", color: "#14112E", marginBottom: 8 }}>
                {item.anchor} <span aria-hidden="true" style={{ color: "#696CFF" }}>→</span>
              </h3>
              <p style={{ fontSize: 14.5, lineHeight: 1.6, color: "#5C5976", margin: 0 }}>{item.blurb}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* SEO LINK MAP — gives crawlers (and users) a path from the homepage
          into every marketing/SEO page. */}
      <nav
        aria-label="Explore Prizo"
        className="prizo-pad"
        style={{ maxWidth: 1200, margin: "0 auto", padding: "10px 32px 8px", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 28, borderTop: "1px solid #EDEBF7" }}
      >
        {SEO_ROUTE_GROUPS.map((group) => (
          <div key={group.title} style={{ minWidth: 0 }}>
            <h3 style={{ fontFamily: "'Bricolage Grotesque'", fontWeight: 700, fontSize: 15, color: "#14112E", margin: "18px 0 12px" }}>
              <Link to={group.hubPath} className="prizo-link" style={{ textDecoration: "none", color: "inherit" }}>
                {group.title}
              </Link>
            </h3>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
              {group.routes.map((route) => (
                <li key={route.path}>
                  <Link
                    to={route.path}
                    className="prizo-link"
                    style={{ textDecoration: "none", color: "#5C5976", fontSize: 13.5, fontWeight: 500 }}
                  >
                    {route.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* FOOTER */}
      <footer className="prizo-pad" style={{ maxWidth: 1200, margin: "0 auto", padding: "36px 32px 50px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 18, borderTop: "1px solid #EDEBF7" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <PrizoLogo width={56} height={37} />
          <span style={{ color: "#9A98AE", fontSize: 14 }}>© 2026 — Gamify your purchase experience</span>
        </div>
        <div style={{ display: "flex", gap: 24, fontSize: 14, color: "#5C5976", fontWeight: 600 }}>
          <Link to="/faq" className="prizo-link" style={{ textDecoration: "none", color: "inherit" }}>FAQ</Link>
          <Link to="/privacy" className="prizo-link" style={{ textDecoration: "none", color: "inherit" }}>Privacy</Link>
          <Link to="/terms" className="prizo-link" style={{ textDecoration: "none", color: "inherit" }}>Terms</Link>
          <a href={SHOPIFY_APP_URL} target="_blank" rel="noopener noreferrer" className="prizo-link" style={{ textDecoration: "none", color: "inherit" }}>Shopify App Store</a>
        </div>
      </footer>

      {/* Video Modal — "See it in action" */}
      {videoModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-[200] p-4" style={{ background: "rgba(0,0,0,.75)" }}>
          <div className="relative w-full max-w-4xl bg-white rounded-2xl overflow-hidden">
            <button onClick={() => setVideoModalOpen(false)} className="absolute top-4 right-4 z-10 w-10 h-10 bg-black bg-opacity-50 text-white rounded-full flex items-center justify-center hover:bg-opacity-75 transition-all" aria-label="Close">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
              <iframe src="https://www.youtube.com/embed/OBSB40Cub70?autoplay=1" title="Prizo Demo Video" className="absolute top-0 left-0 w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LandingPage;
